/**
 * Facture d’impayés : uniquement les mois réellement en retard
 * (même logique que le badge « Mois impayés » / bandeau).
 */
import { isExceptionalMonthValue } from './exceptionalContributions';
import { isAdhesionContributionMonth } from './memberDashboardPersonalStats';

const PAID_STATUSES = new Set(['completed', 'paid', 'approved']);
const UNPAID_STATUSES = new Set(['pending', 'unpaid', 'awaiting', 'draft']);

function contributionPersonId(c) {
  return String(
    c?.person_id ||
      c?.member_id ||
      c?.person?.id ||
      c?.member?.id ||
      c?.metadata?.person_id ||
      c?.metadata?.member_id ||
      ''
  ).trim();
}

function regularMonthKey(c) {
  const m = String(c?.month || '').trim();
  if (!m || isAdhesionContributionMonth(m) || isExceptionalMonthValue(m)) return '';
  if (!/^\d{4}-\d{2}/.test(m)) return '';
  return m.slice(0, 7);
}

function isMemberActive(member) {
  if (!member || typeof member !== 'object') return true;
  const raw = member.status ?? member.active;
  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0) return false;
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return true;
  return s === 'active' || s === 'actif' || s === 'true' || s === '1';
}

function monthKeyFromDate(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Mois YYYY-MM encore dus pour un membre — badge « Mois impayés » / avis :
 * fenêtre [max(adhésion, 1er mois payé) … maintenant], sans inventer de mois avant
 * le début réel des cotisations, ni forcer le compteur API quand l’historique local suffit.
 */
export function listUnpaidRegularMonthKeys(
  contributions,
  { personId = null, maxLookback = 36, now = new Date(), joinMonth = null, targetCount = null } = {}
) {
  const pid = String(personId || '').trim();
  const rows = (Array.isArray(contributions) ? contributions : []).filter((c) => {
    if (!pid) return true;
    return contributionPersonId(c) === pid;
  });

  const paid = new Set();
  const unpaidExisting = new Set();
  for (const c of rows) {
    const ym = regularMonthKey(c);
    if (!ym) continue;
    const st = String(c?.status || '').toLowerCase().trim();
    if (PAID_STATUSES.has(st)) paid.add(ym);
    else if (UNPAID_STATUSES.has(st)) unpaidExisting.add(ym);
  }

  const paidSorted = [...paid].sort();
  const firstPaidYm = paidSorted[0] || '';
  const lastPaidYm = paidSorted[paidSorted.length - 1] || '';
  const currentYm = monthKeyFromDate(now);

  if (lastPaidYm && lastPaidYm >= currentYm) return [];

  const joinYm = (() => {
    const raw = String(joinMonth || '').trim();
    if (/^\d{4}-\d{2}/.test(raw)) return raw.slice(0, 7);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 7);
    return '';
  })();

  // Ne pas remonter avant le 1er paiement réel (évite 20+ mois fantômes).
  let startYm = joinYm || firstPaidYm || '';
  if (firstPaidYm && (!startYm || startYm < firstPaidYm)) {
    startYm = firstPaidYm;
  }

  const keys = new Set();
  const lookback = Math.min(36, Math.max(1, Number(maxLookback) || 36));
  for (let i = 0; i < lookback; i += 1) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = monthKeyFromDate(dt);
    if (startYm && ym < startYm) break;
    if (paid.has(ym)) continue;
    keys.add(ym);
  }

  for (const ym of unpaidExisting) {
    if (startYm && ym < startYm) continue;
    if (paid.has(ym)) continue;
    keys.add(ym);
  }

  // Extension API uniquement si aucun mois payé local (historique incomplet).
  const want = Number(targetCount);
  if (paid.size === 0 && Number.isFinite(want) && want > keys.size) {
    const extendTo = Math.min(36, Math.max(lookback, want));
    for (let i = 0; i < extendTo && keys.size < want; i += 1) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const ym = monthKeyFromDate(dt);
      if (startYm && ym < startYm) break;
      if (paid.has(ym)) continue;
      keys.add(ym);
    }
  }

  return [...keys].sort();
}

/**
 * Lignes facture : réutilise les cotisations non payées existantes, sinon lignes virtuelles
 * au montant mensuel des paramètres (pas de création en base).
 */
export function buildArrearsInvoiceContributionRows({
  contributions,
  personId,
  monthlyAmount,
  monthKeys = null,
  joinMonth = null,
} = {}) {
  const pid = String(personId || '').trim();
  const keys =
    Array.isArray(monthKeys) && monthKeys.length
      ? monthKeys.filter((k) => /^\d{4}-\d{2}$/.test(String(k || '').trim())).map((k) => String(k).slice(0, 7))
      : listUnpaidRegularMonthKeys(contributions, { personId: pid, joinMonth });

  const rows = (Array.isArray(contributions) ? contributions : []).filter((c) => {
    if (!pid) return true;
    return contributionPersonId(c) === pid;
  });

  const unpaidByMonth = new Map();
  for (const c of rows) {
    const ym = regularMonthKey(c);
    if (!ym) continue;
    const st = String(c?.status || '').toLowerCase();
    if (!UNPAID_STATUSES.has(st)) continue;
    if (!unpaidByMonth.has(ym)) unpaidByMonth.set(ym, c);
  }

  const fallbackAmount = Number(monthlyAmount);
  const safeAmount = Number.isFinite(fallbackAmount) && fallbackAmount > 0 ? fallbackAmount : 0;

  return keys.map((ym) => {
    const existing = unpaidByMonth.get(ym);
    if (existing) {
      return {
        id: existing.id,
        month: ym,
        amount: Number(existing.amount) || safeAmount,
        payment_method: existing.payment_method || '',
        status: existing.status || 'unpaid',
        notes: existing.notes || '',
        person_id: pid || contributionPersonId(existing),
        _virtual: false,
      };
    }
    return {
      id: `arrears:${pid || 'member'}:${ym}`,
      month: ym,
      amount: safeAmount,
      payment_method: '',
      status: 'unpaid',
      notes: '',
      person_id: pid,
      _virtual: true,
    };
  });
}

export function memberIsActiveForArrearsInvoice(member) {
  return isMemberActive(member);
}

/** Date d’échéance d’un mois YYYY-MM (jour limite org., fin de journée locale). */
export function getMonthPaymentDueDate(monthYm, deadlineDay = 15) {
  const day = Math.min(28, Math.max(1, Number(deadlineDay) || 15));
  const monthKey = String(monthYm || '').trim().slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(monthKey)) return null;
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, day, 23, 59, 59, 999);
}

/** `due` = échéance pas encore dépassée ; `overdue` = en retard. */
export function classifyPaymentDueState(monthYm, { deadlineDay = 15, now = new Date() } = {}) {
  const due = getMonthPaymentDueDate(monthYm, deadlineDay);
  if (!due) return 'due';
  return now.getTime() > due.getTime() ? 'overdue' : 'due';
}

/**
 * Enrichit les lignes d’avis (dues / en retard) avec dueState + dueDate.
 * Ce sont les « paiements dus ou en retard » = avis de cotisation en cours.
 */
export function enrichDuePaymentRows(rows, { deadlineDay = 15, now = new Date() } = {}) {
  return (Array.isArray(rows) ? rows : []).map((r) => {
    const month = String(r?.month || '').trim().slice(0, 7);
    return {
      ...r,
      month,
      dueState: classifyPaymentDueState(month, { deadlineDay, now }),
      dueDate: getMonthPaymentDueDate(month, deadlineDay),
    };
  });
}
